import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {
  Container,
  Dialog,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

import conf from "../../conf";
import format from "../utils/Role";
import InfoChip from "../utils/InfoChip";
import { IDP, User } from "../auth/AuthProvider";
import SectionHeader from "../utils/SectionHeader";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import { useVaultInvite } from "../../hooks/vault/base";

const SelectRole = ({
  label = "Role",
  value,
  onChange,
  disabled = false
}: {
  value: string;
  label?: string;
  disabled?: boolean;
  onChange: (e: User["activeOrganization"]["role"]) => void;
}) => {
  const handleChange = (event: SelectChangeEvent) => onChange(event.target.value as User["activeOrganization"]["role"]);

  return (
    <Box sx={{ minWidth: 120 }}>
      <FormControl fullWidth>
        <InputLabel id="role-select-label">{label}</InputLabel>

        <Select
          id="role-select"
          labelId="role-select-label"
          label={label}
          value={value}
          disabled={disabled}
          onChange={handleChange}
        >
          {conf.ROLES.map((role) => (
            <MenuItem key={role} value={role}>
              {format(role)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const SelectProvider = ({
  label = "Identity provider",
  value,
  disabled = false,
  onChange
}: {
  value: User["idp"];
  label?: string;
  disabled?: boolean;
  onChange: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, value: IDP) => void;
}) => {
  return (
    <Box sx={{ minWidth: 120 }}>
      <InputLabel id="idp-select">{label}</InputLabel>

      <FormGroup id="idp-select" row>
        {conf.IDPS.map((props) => (
          <FormControlLabel
            key={props.value}
            label={props.label}
            disabled={props.disabled || disabled}
            control={
              <Checkbox checked={value.includes(props.value as IDP)} onClick={(e) => onChange(e, props.value as IDP)} />
            }
          />
        ))}
      </FormGroup>
    </Box>
  );
};

const InviteView = (props: any) => {
  const { open, onClose } = props;

  const [locked, setLocked] = useState(true);

  const [idp, setIdp] = useState<User["idp"]>(["LOCAL"]);
  const [role, setRole] = useState<User["activeOrganization"]["role"]>("org.auditor");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");

  const invite = useVaultInvite();

  const resetForm = () => {
    setIdp(["LOCAL"]);
    setRole("org.auditor");
    setEmail("");
    setComment("");

    setLocked(true);
  };

  const onChangeIdp = (e: React.MouseEvent<unknown>, value: IDP) => {
    const current = idp.indexOf(value);
    let edited: IDP[] = [];

    if (current === -1) {
      edited = edited.concat(idp, value);
    } else if (current === 0) {
      edited = edited.concat(idp.slice(1));
    } else if (current === idp.length - 1) {
      edited = edited.concat(idp.slice(0, -1));
    } else if (current > 0) {
      edited = edited.concat(idp.slice(0, current), idp.slice(current + 1));
    }

    setIdp(edited);
  };

  const onConfirm = () => invite.mutate({ email, role, comment, idp });

  const warning = (
    <>
      This action will invite <b>{email}</b> to join your organization as a(n) <b>{format(role)}</b>.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  const endAdornment = (
    <InputAdornment position="start">
      <InfoChip size="medium" info="This comment will be attached to the email sent to your new user." />
    </InputAdornment>
  );

  useEffect(() => {
    if (
      !!email &&
      email.includes("@") &&
      (email.includes("com") || email.includes("org") || email.includes("net") || email.includes("gov"))
    ) {
      setLocked(false);
    } else {
      setLocked(true);
    }
  }, [email]);

  useEffect(() => {
    if (invite.isSuccess) {
      resetForm();
      onClose();
    }
  }, [invite.isSuccess]);

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key="invite-header"
          title="Invite"
          icon={<PersonAddAlt1Icon />}
          buttons={[
            <Button key={"invite-reset"} size="small" color="inherit" onClick={resetForm}>
              Reset
            </Button>,

            <Button key={"invite-close"} size="small" color="inherit" onClick={onClose}>
              <CloseIcon />
            </Button>
          ]}
        />

        <Box component="form" noValidate>
          <Grid container spacing={2} sx={{ mt: 1, color: "text.primary" }}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                autoComplete="email"
                label="Email address"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                disabled={invite.isPending}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <SelectRole value={role} onChange={setRole} disabled={invite.isPending} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="comment"
                name="comment"
                label="Comment"
                value={comment}
                onChange={(e: any) => setComment(e.target.value)}
                InputProps={{ endAdornment }}
                disabled={invite.isPending}
              />
            </Grid>

            <Grid item xs={12}>
              <SelectProvider value={idp} onChange={onChangeIdp} disabled={invite.isPending} />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <ButtonWithConfirm
              type="invite"
              label="Invite"
              status={invite.status}
              message={warning}
              disabled={locked}
              onConfirm={onConfirm}
              isLoading={invite.isPending}
            />
          </Stack>
        </Box>
      </Container>
    </Dialog>
  );
};

const Invite = ({ disabled = false }: { disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div>
      <Button
        color="secondary"
        variant="contained"
        disabled={disabled}
        onClick={handleOpen}
        startIcon={<PersonAddAlt1Icon />}
      >
        Invite
      </Button>

      <InviteView open={open} onClose={handleClose} />
    </div>
  );
};

export default Invite;
